import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import BackToTop from '../home/components/BackToTop';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0E0503] text-white py-6 sm:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#E48523] transition-colors mb-4 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Privacy Policy</h1>
          <p className="text-sm text-white/60 mt-2">
            Indigenous Engagement and Facilitation Pty Ltd
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Last Updated */}
          <p className="text-sm text-[#3D1203]/60 mb-6">
            <strong>Last Updated:</strong> January 2025
          </p>

          {/* Cross-link */}
          <div className="flex items-center gap-3 bg-[#FFF8F0] border border-[#E48523]/30 rounded-lg px-4 py-3 mb-8">
            <i className="ri-file-text-line text-[#E48523] w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
            <p className="text-sm text-[#3D1203] m-0">
              Looking for our service agreement?{' '}
              <Link to="/terms" className="text-[#E48523] font-medium hover:underline cursor-pointer">
                View our Terms of Service &rarr;
              </Link>
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">1. Introduction</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Indigenous Engagement and Facilitation Pty Ltd (ABN: 51 669 512 823 | ACN: 669 512 823) ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or enrol in our training courses.
            </p>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              By using our services, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">2. Information We Collect</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              We may collect the following types of information:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] text-sm sm:text-base">
              <li><strong>Personal Information:</strong> Name, email address, phone number, organisation name, and job title when you enquire about or enrol in our courses.</li>
              <li><strong>Payment Information:</strong> Billing details and payment card information processed securely through third-party payment processors.</li>
              <li><strong>Usage Data:</strong> Information about how you access and use our website, including IP address, browser type, pages visited, and time spent on pages.</li>
              <li><strong>Cookies and Tracking:</strong> We use cookies and similar tracking technologies to monitor activity on our website and store certain information.</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">3. How We Use Your Information</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] text-sm sm:text-base">
              <li>To process course enrolments and deliver training services</li>
              <li>To communicate with you about your enrolment, course updates, and administrative matters</li>
              <li>To process payments and maintain financial records</li>
              <li>To improve our website, courses, and customer service</li>
              <li>To send marketing communications (with your consent, which you may withdraw at any time)</li>
              <li>To comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          {/* Disclosure of Information */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">4. Disclosure of Information</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              We may share your information with:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] text-sm sm:text-base">
              <li><strong>Service Providers:</strong> Third-party companies that assist us in operating our website, processing payments, or delivering our services.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, or acquisition.</li>
            </ul>
            <p className="text-[#3D1203] leading-relaxed mt-4 text-sm sm:text-base">
              We do not sell, trade, or rent your personal information to third parties for marketing purposes.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">5. Data Security</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              We implement appropriate technical and organisational security measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">6. Cookies and Tracking Technologies</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
            </p>
          </section>

          {/* Third-Party Links */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">7. Third-Party Links</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">8. Your Rights</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              Under Australian privacy law, you have the right to:
            </p>
            <ul className="list-disc pl-5 sm:pl-6 space-y-2 text-[#3D1203] text-sm sm:text-base">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information (subject to legal obligations)</li>
              <li>Opt-out of marketing communications</li>
              <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
            </ul>
          </section>

          {/* Contact Us */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">9. Contact Us</h2>
            <p className="text-[#3D1203] leading-relaxed mb-4 text-sm sm:text-base">
              If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-[#FFF8F0] border-l-4 border-[#E48523] p-4 sm:p-6 rounded-lg">
              <p className="text-[#0E0503] font-semibold mb-2 text-sm sm:text-base">
                Indigenous Engagement and Facilitation Pty Ltd
              </p>
              <p className="text-[#3D1203] text-xs sm:text-sm mb-1">
                <strong>ABN:</strong> 51 669 512 823 | <strong>ACN:</strong> 669 512 823
              </p>
              <p className="text-[#3D1203] text-xs sm:text-sm mb-1">
                <strong>Email:</strong>{' '}
                <a 
                  href="mailto:info@indigenousfacilitation.com.au" 
                  className="text-[#E48523] hover:underline cursor-pointer"
                >
                  info@indigenousfacilitation.com.au
                </a>
              </p>
              <p className="text-[#3D1203] text-xs sm:text-sm">
                <strong>Location:</strong> Australia
              </p>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8 sm:mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0E0503] mb-3 sm:mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-[#3D1203] leading-relaxed text-sm sm:text-base">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

        </div>
      </main>

      <BackToTop />
      <Footer />
    </div>
  );
}